import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from '../lib/toast';
import Sidebar from '../components/layout/Sidebar';
import { Plus, Users, Trash2, Merge, Split, ShoppingBag, X, Check, Edit } from 'lucide-react';

const Tables = () => {
  const { api, user } = useAuth();
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTable, setEditingTable] = useState(null);
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [splitDialogOpen, setSplitDialogOpen] = useState(false);
  const [selectedTableOrders, setSelectedTableOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [newCustomerName, setNewCustomerName] = useState('');
  
  // For merge
  const [selectedTablesForMerge, setSelectedTablesForMerge] = useState([]);
  const [targetTable, setTargetTable] = useState(null);
  
  const [formData, setFormData] = useState({
    number: '',
    capacity: 4,
    area: 'Principal'
  });

  // Check if user is admin
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    fetchTables();
  }, []);

  const fetchTables = async () => {
    try {
      const response = await api.get('/tables');
      setTables(response.data);
    } catch (error) {
      toast.error('Error al cargar mesas');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTable) {
        await api.put(`/tables/${editingTable.table_id}`, {
          number: parseInt(formData.number),
          capacity: parseInt(formData.capacity),
          area: formData.area
        });
        toast.success('Mesa actualizada');
      } else {
        await api.post('/tables', {
          number: parseInt(formData.number),
          capacity: parseInt(formData.capacity),
          area: formData.area
        });
        toast.success('Mesa creada exitosamente');
      }
      setDialogOpen(false);
      setEditingTable(null);
      setFormData({ number: '', capacity: 4, area: 'Principal' });
      fetchTables();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar mesa');
    }
  };

  const openEditDialog = (table) => {
    setEditingTable(table);
    setFormData({
      number: table.number.toString(),
      capacity: table.capacity || 4,
      area: table.area || 'Principal'
    });
    setDialogOpen(true);
  };

  const openCreateDialog = () => {
    setEditingTable(null);
    setFormData({ number: '', capacity: 4, area: 'Principal' });
    setDialogOpen(true);
  };

  const handleStatusChange = async (tableId, newStatus) => {
    try {
      await api.put(`/tables/${tableId}/status?status=${newStatus}`);
      toast.success('Estado actualizado');
      fetchTables();
    } catch (error) {
      toast.error('Error al actualizar estado');
    }
  };

  const handleDelete = async (tableId) => {
    if (!window.confirm('¿Eliminar esta mesa?')) return;
    try {
      await api.delete(`/tables/${tableId}`);
      toast.success('Mesa eliminada');
      fetchTables();
    } catch (error) {
      toast.error('Error al eliminar mesa');
    }
  };

  const handleMergeTables = async () => {
    if (selectedTablesForMerge.length < 2) {
      toast.error('Selecciona al menos 2 mesas para unir');
      return;
    }
    if (!targetTable) {
      toast.error('Selecciona la mesa destino');
      return;
    }

    try {
      const response = await api.post('/tables/merge', {
        source_table_ids: selectedTablesForMerge.filter(id => id !== targetTable),
        target_table_id: targetTable
      });
      toast.success(response.data.message);
      setMergeDialogOpen(false);
      setSelectedTablesForMerge([]);
      setTargetTable(null);
      fetchTables();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al unir mesas');
    }
  };

  const openSplitDialog = async (table) => {
    try {
      const response = await api.get(`/tables/${table.table_id}/orders`);
      setSelectedTableOrders(response.data);
      if (response.data.length === 0) {
        toast.error('Esta mesa no tiene órdenes activas');
        return;
      }
      setSplitDialogOpen(true);
    } catch (error) {
      toast.error('Error al cargar órdenes');
    }
  };

  const handleSplitBill = async () => {
    if (!selectedOrder || selectedItems.length === 0) {
      toast.error('Selecciona una orden y al menos un producto');
      return;
    }

    try {
      const response = await api.post(`/orders/${selectedOrder.order_id}/split`, {
        order_id: selectedOrder.order_id,
        items_to_split: selectedItems.map(item => ({
          product_id: item.product_id,
          quantity: item.splitQty || 1
        })),
        new_customer_name: newCustomerName || undefined
      });
      toast.success(response.data.message);
      setSplitDialogOpen(false);
      setSelectedOrder(null);
      setSelectedItems([]);
      setNewCustomerName('');
      fetchTables();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al dividir cuenta');
    }
  };

  const toggleItemForSplit = (item) => {
    const exists = selectedItems.find(i => i.product_id === item.product_id);
    if (exists) {
      setSelectedItems(selectedItems.filter(i => i.product_id !== item.product_id));
    } else {
      setSelectedItems([...selectedItems, { ...item, splitQty: 1 }]);
    }
  };

  const updateSplitQty = (productId, qty) => {
    setSelectedItems(selectedItems.map(item => 
      item.product_id === productId ? { ...item, splitQty: Math.max(1, Math.min(qty, item.quantity)) } : item
    ));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'available': return 'bg-green-50 border-green-400 text-green-700';
      case 'occupied': return 'bg-orange-50 border-orange-400 text-orange-700';
      case 'reserved': return 'bg-blue-50 border-blue-400 text-blue-700';
      default: return 'bg-muted border-border';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'available': return 'Disponible';
      case 'occupied': return 'Ocupada';
      case 'reserved': return 'Reservada';
      default: return status;
    }
  };

  // Group tables by area
  const tablesByArea = tables.reduce((acc, table) => {
    const area = table.area || 'Sin Área';
    if (!acc[area]) acc[area] = [];
    acc[area].push(table);
    return acc;
  }, {});

  const occupiedTables = tables.filter(t => t.status === 'occupied');

  return (
    <div className="min-h-screen bg-background flex">
      <Sidebar />
      
      <main className="flex-1 md:ml-64 p-4 md:p-8 pt-20 md:pt-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-3xl font-chivo font-extrabold" data-testid="tables-title">
                Gestión de Mesas
              </h1>
              <p className="text-muted-foreground">
                {tables.filter(t => t.status === 'available').length} de {tables.length} disponibles
              </p>
            </div>
            
            <div className="flex flex-wrap gap-2">
              {/* Merge Tables Button */}
              <Dialog open={mergeDialogOpen} onOpenChange={setMergeDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" disabled={occupiedTables.length < 2} data-testid="merge-tables-btn">
                    <Merge className="w-4 h-4 mr-2" />
                    Unir Mesas
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle className="font-chivo">Unir Mesas</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <p className="text-sm text-muted-foreground">
                      Selecciona las mesas que deseas unir. Las órdenes se moverán a la mesa destino.
                    </p>
                    <div className="space-y-2">
                      <Label>Mesas a Unir</Label>
                      <div className="grid grid-cols-4 gap-2">
                        {occupiedTables.map(table => (
                          <button
                            key={table.table_id}
                            type="button"
                            className={`p-3 rounded-lg border-2 text-center transition-all ${
                              selectedTablesForMerge.includes(table.table_id)
                                ? 'border-orange-500 bg-orange-50'
                                : 'border-border hover:border-orange-300'
                            }`}
                            onClick={() => {
                              if (selectedTablesForMerge.includes(table.table_id)) {
                                setSelectedTablesForMerge(selectedTablesForMerge.filter(id => id !== table.table_id));
                                if (targetTable === table.table_id) setTargetTable(null);
                              } else {
                                setSelectedTablesForMerge([...selectedTablesForMerge, table.table_id]);
                              }
                            }}
                          >
                            <span className="font-bold">{table.number}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    {selectedTablesForMerge.length >= 2 && (
                      <div className="space-y-2">
                        <Label>Mesa Destino</Label>
                        <div className="grid grid-cols-4 gap-2">
                          {selectedTablesForMerge.map(tableId => {
                            const table = tables.find(t => t.table_id === tableId);
                            return table ? (
                              <button
                                key={tableId}
                                type="button"
                                className={`p-3 rounded-lg border-2 text-center transition-all ${
                                  targetTable === tableId
                                    ? 'border-green-500 bg-green-50'
                                    : 'border-border hover:border-green-300'
                                }`}
                                onClick={() => setTargetTable(tableId)}
                              >
                                <span className="font-bold">{table.number}</span>
                                {targetTable === tableId && <Check className="w-4 h-4 mx-auto mt-1 text-green-600" />}
                              </button>
                            ) : null;
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setMergeDialogOpen(false)}>Cancelar</Button>
                    <Button 
                      className="bg-orange-500 hover:bg-orange-600"
                      onClick={handleMergeTables}
                      disabled={selectedTablesForMerge.length < 2 || !targetTable}
                    >
                      Unir Mesas
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

              {/* New Table Button - Admin only */}
              {isAdmin && (
                <Dialog open={dialogOpen} onOpenChange={(open) => {
                  setDialogOpen(open);
                  if (!open) setEditingTable(null);
                }}>
                  <DialogTrigger asChild>
                    <Button className="bg-orange-500 text-white hover:bg-orange-600" onClick={openCreateDialog} data-testid="add-table-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Nueva Mesa
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle className="font-chivo">{editingTable ? 'Editar Mesa' : 'Nueva Mesa'}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="number">Número de Mesa</Label>
                        <Input
                          id="number"
                          type="number"
                          value={formData.number}
                          onChange={(e) => setFormData({ ...formData, number: e.target.value })}
                          required
                          data-testid="table-number-input"
                        />
                        <p className="text-xs text-muted-foreground">Solo el número (ej: 1, 2, 3)</p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="capacity">Capacidad</Label>
                        <Input
                          id="capacity"
                          type="number"
                          value={formData.capacity}
                          onChange={(e) => setFormData({ ...formData, capacity: e.target.value })}
                          required
                          data-testid="table-capacity-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="area">Área</Label>
                        <Input
                          id="area"
                          value={formData.area}
                          onChange={(e) => setFormData({ ...formData, area: e.target.value })}
                          placeholder="Principal, Terraza, VIP, Colina Baja..."
                          data-testid="table-area-input"
                        />
                        <p className="text-xs text-muted-foreground">Nombre del área donde está la mesa</p>
                      </div>
                      <Button type="submit" className="w-full bg-orange-500 text-white hover:bg-orange-600" data-testid="save-table-btn">
                        {editingTable ? 'Guardar Cambios' : 'Crear Mesa'}
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              )}
            </div>
          </div>

          {/* Legend */}
          <div className="flex flex-wrap gap-4 mb-6">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-green-100 border-2 border-green-400" />
              <span className="text-sm">Disponible</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-orange-100 border-2 border-orange-400" />
              <span className="text-sm">Ocupada</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-blue-100 border-2 border-blue-400" />
              <span className="text-sm">Reservada</span>
            </div>
          </div>

          {/* Tables by Area */}
          {Object.entries(tablesByArea).map(([area, areaTables]) => (
            <div key={area} className="mb-8">
              <h2 className="text-xl font-chivo font-extrabold mb-4">{area}</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {areaTables.map((table) => (
                  <Card 
                    key={table.table_id}
                    className={`border-2 transition-all hover:shadow-lg ${getStatusColor(table.status)}`}
                    data-testid={`table-card-${table.number}`}
                  >
                    <CardContent className="p-4">
                      <div className="text-center mb-3">
                        <span className="text-3xl font-chivo font-extrabold">{table.number}</span>
                      </div>
                      <div className="flex items-center justify-center gap-1 text-sm mb-3">
                        <Users className="w-4 h-4" />
                        <span>{table.capacity}</span>
                      </div>
                      <div className="text-center mb-3">
                        <span className="text-xs font-medium px-2 py-1 rounded-full bg-white/50">
                          {getStatusLabel(table.status)}
                        </span>
                      </div>
                      <div className="flex flex-col gap-1">
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="flex-1 h-8 text-xs"
                            onClick={() => handleStatusChange(
                              table.table_id, 
                              table.status === 'available' ? 'occupied' : 'available'
                            )}
                            data-testid={`toggle-status-${table.number}`}
                          >
                            {table.status === 'available' ? 'Ocupar' : 'Liberar'}
                          </Button>
                          {isAdmin && (
                            <>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-8 w-8 p-0 text-blue-600 hover:text-blue-700"
                                onClick={() => openEditDialog(table)}
                                data-testid={`edit-table-${table.number}`}
                              >
                                <Edit className="w-3 h-3" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                                onClick={() => handleDelete(table.table_id)}
                                data-testid={`delete-table-${table.number}`}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </>
                          )}
                        </div>
                        {table.status === 'occupied' && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8 text-xs w-full"
                            onClick={() => openSplitDialog(table)}
                            data-testid={`split-bill-${table.number}`}
                          >
                            <Split className="w-3 h-3 mr-1" />
                            Dividir Cuenta
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ))}

          {tables.length === 0 && !loading && (
            <Card className="border-dashed border-2">
              <CardContent className="py-12 text-center">
                <p className="text-muted-foreground mb-4">No hay mesas configuradas</p>
                <Button 
                  className="bg-orange-500 text-white hover:bg-orange-600"
                  onClick={() => setDialogOpen(true)}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Agregar Primera Mesa
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </main>

      {/* Split Bill Dialog */}
      <Dialog open={splitDialogOpen} onOpenChange={setSplitDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-chivo">Dividir Cuenta</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {selectedTableOrders.length > 0 && !selectedOrder && (
              <>
                <p className="text-sm text-muted-foreground">Selecciona la orden que deseas dividir:</p>
                <div className="space-y-2">
                  {selectedTableOrders.map(order => (
                    <button
                      key={order.order_id}
                      type="button"
                      className="w-full p-3 rounded-lg border-2 text-left hover:border-orange-300 transition-all"
                      onClick={() => setSelectedOrder(order)}
                    >
                      <div className="flex justify-between items-center">
                        <span className="font-bold">Orden #{order.order_number}</span>
                        <span className="text-orange-600 font-bold">${order.total?.toFixed(2)}</span>
                      </div>
                      <p className="text-sm text-muted-foreground">{order.items?.length || 0} productos</p>
                    </button>
                  ))}
                </div>
              </>
            )}

            {selectedOrder && (
              <>
                <div className="flex items-center justify-between">
                  <h3 className="font-bold">Orden #{selectedOrder.order_number}</h3>
                  <Button variant="ghost" size="sm" onClick={() => { setSelectedOrder(null); setSelectedItems([]); }}>
                    <X className="w-4 h-4" /> Cambiar
                  </Button>
                </div>
                <p className="text-sm text-muted-foreground">Selecciona los productos para la nueva cuenta:</p>
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {selectedOrder.items?.map(item => {
                    const isSelected = selectedItems.find(i => i.product_id === item.product_id);
                    return (
                      <div 
                        key={item.product_id}
                        className={`p-3 rounded-lg border-2 transition-all ${isSelected ? 'border-orange-500 bg-orange-50' : 'border-border'}`}
                      >
                        <div className="flex items-center gap-3">
                          <Checkbox
                            checked={!!isSelected}
                            onCheckedChange={() => toggleItemForSplit(item)}
                          />
                          <div className="flex-1">
                            <p className="font-medium text-sm">{item.product_name}</p>
                            <p className="text-xs text-muted-foreground">
                              ${item.unit_price} x {item.quantity} = ${item.subtotal?.toFixed(2)}
                            </p>
                          </div>
                          {isSelected && item.quantity > 1 && (
                            <div className="flex items-center gap-2">
                              <Label className="text-xs">Cantidad:</Label>
                              <Input
                                type="number"
                                min="1"
                                max={item.quantity}
                                value={isSelected.splitQty}
                                onChange={(e) => updateSplitQty(item.product_id, parseInt(e.target.value) || 1)}
                                className="w-16 h-8 text-center"
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                {selectedItems.length > 0 && (
                  <div className="space-y-3 pt-4 border-t">
                    <div className="flex justify-between text-sm">
                      <span>Total Nueva Cuenta:</span>
                      <span className="font-bold text-orange-600">
                        ${selectedItems.reduce((sum, item) => sum + (item.unit_price * (item.splitQty || 1)), 0).toFixed(2)}
                      </span>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm">Nombre del Cliente (opcional)</Label>
                      <Input
                        value={newCustomerName}
                        onChange={(e) => setNewCustomerName(e.target.value)}
                        placeholder="Nombre para la nueva cuenta..."
                      />
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setSplitDialogOpen(false); setSelectedOrder(null); setSelectedItems([]); }}>
              Cancelar
            </Button>
            <Button 
              className="bg-orange-500 hover:bg-orange-600"
              onClick={handleSplitBill}
              disabled={!selectedOrder || selectedItems.length === 0}
            >
              <Split className="w-4 h-4 mr-2" />
              Dividir Cuenta
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Tables;
